import * as React from "react"

import { useTranslations } from "@/i18n"
import { useWidgetMenu } from "@ibada/dashboard-engine"

import { WidgetBoundary } from "@/features/finance/overview/components/widget-boundary"
import { OverviewRoutes, useOverviewNav } from "@/features/finance/overview/lib/overview-nav"
import {
  PerformanceRowList,
  PerformanceRowsSkeleton,
  toneForHealth,
  useBudgetPerformance,
  type PerformanceRow,
} from "@/features/finance/overview/widgets/budget-performance-shared"

const MAX_ROWS = 5

/**
 * Income Budget vs Actual — planned against received per INCOME ACCOUNT.
 *
 * This answers "how did this source of income perform?", and it is measured from the transactions recorded
 * against that account. If money is later allocated out of the account's linked fund, the fund gets smaller
 * and THIS DOES NOT MOVE — income that arrived, arrived. Fund movement is the Fund Utilization widget's job.
 */
export function IncomeBudgetVsActualWidget() {
  const t = useTranslations("finance.overview")
  const go = useOverviewNav()
  const query = useBudgetPerformance()

  const rows = React.useMemo<PerformanceRow[] | undefined>(() => {
    if (!query.data) return undefined
    return [...query.data.byIncomeAccount]
      .sort((a, b) => (b.utilizationPct ?? 0) - (a.utilizationPct ?? 0))
      .slice(0, MAX_ROWS)
      .map((account) => ({
        id: account.accountId,
        label: account.accountName ?? account.accountId,
        planned: account.planned,
        actual: account.actual,
        utilizationPct: account.utilizationPct,
        tone: toneForHealth(account.healthStatus),
      }))
  }, [query.data])

  useWidgetMenu({
    refresh: () => void query.refetch(),
    viewDetails: go.canNavigateTo(OverviewRoutes.budgets) ? () => go(OverviewRoutes.budgets) : undefined,
  })

  return (
    <WidgetBoundary
      isLoading={query.isLoading}
      isError={query.isError}
      isFetching={query.isFetching}
      data={rows}
      isEmpty={(list) => list.length === 0}
      skeleton={<PerformanceRowsSkeleton />}
      errorTitle={t("widgets.incomeBudgetVsActual.error")}
      onRetry={() => void query.refetch()}
      retryLabel={t("widgets.common.retry")}
      emptyTitle={t("widgets.incomeBudgetVsActual.empty.title")}
    >
      {(list) => <PerformanceRowList rows={list} currency={query.data?.currency ?? ""} />}
    </WidgetBoundary>
  )
}
